import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Eye, EyeOff, Home, Film, CalendarDays, Briefcase, Info, Compass, Palette } from 'lucide-react';
import { useAdminLang } from '../../hooks/useAdminLang';
import FilmsLibresToggle from './FilmsLibresToggle';
import { refreshSectionsVisibility } from '../../hooks/useSectionsVisibility';

const T = {
  fr: {
    title: 'Visibilité des sections', sub: 'Masque ou réaffiche des sections de l\'app en un clic. Les contenus restent en base et réapparaissent quand tu réactives.',
    home: 'Onglet « Accueil » (feed)', homeSub: 'Masque tout l\'onglet Accueil. Le bouton story et le burger (Avatar IA, Ajouter des amis) passent dans Découvrir ; les profils n\'affichent plus les contenus partagés ; les pubs Pro s\'affichent au début des stories.',
    mymouv: 'Section MyMouv (évènements)', mymouvSub: 'Réapparaît automatiquement dès qu\'un organisateur crée un évènement.',
    myjob: 'Section MyJob (emplois)', myjobSub: 'Réapparaît automatiquement dès qu\'une offre est publiée.',
    films: 'Section Films libres de droit', visible: 'Visible', hidden: 'Masquée', saved: 'Enregistré',
    explore: 'Section « Explore toute l\'app » (Découvrir)', exploreSub: 'Les raccourcis colorés vers MyTalk, IA, partage et profil en bas de Découvrir.',
    themes: 'Choix du mode d\'affichage (Découvrir)', themesSub: 'Les 3 tuiles Sombre / Clair rose / Clair blanc dans Découvrir. Le choix reste possible dans Réglages.',
  },
  en: {
    title: 'Sections visibility', sub: 'Hide or show app sections in one click. Content stays in the database and comes back when re-enabled.',
    home: '"Home" tab (feed)', homeSub: 'Hides the whole Home tab. The story button and burger (AI Avatar, Add friends) move to Discover; profiles no longer show shared content; Pro ads play at the start of stories.',
    mymouv: 'MyMouv section (events)', mymouvSub: 'Re-appears automatically as soon as an organizer creates an event.',
    myjob: 'MyJob section (jobs)', myjobSub: 'Re-appears automatically as soon as an offer is published.',
    films: 'Public-domain films section', visible: 'Visible', hidden: 'Hidden', saved: 'Saved',
    explore: '"Explore the whole app" section (Discover)', exploreSub: 'The colored shortcuts to MyTalk, AI, sharing and profile at the bottom of Discover.',
    themes: 'Display mode picker (Discover)', themesSub: 'The 3 tiles Dark / Light rose / Light white in Discover. Still available in Settings.',
  },
  es: {
    title: 'Visibilidad de secciones', sub: 'Oculta o muestra secciones de la app con un clic. El contenido se conserva y vuelve al reactivar.',
    home: 'Pestaña «Inicio» (feed)', homeSub: 'Oculta toda la pestaña Inicio. El botón de historia y el menú (Avatar IA, Añadir amigos) pasan a Descubrir; los perfiles ya no muestran contenidos; los anuncios Pro se muestran al inicio de las historias.',
    mymouv: 'Sección MyMouv (eventos)', mymouvSub: 'Reaparece automáticamente cuando un organizador crea un evento.',
    myjob: 'Sección MyJob (empleos)', myjobSub: 'Reaparece automáticamente cuando se publica una oferta.',
    films: 'Sección Películas de dominio público', visible: 'Visible', hidden: 'Oculta', saved: 'Guardado',
    explore: 'Sección «Explora toda la app» (Descubrir)', exploreSub: 'Los accesos de colores a MyTalk, IA, compartir y perfil al final de Descubrir.',
    themes: 'Selector de modo de visualización (Descubrir)', themesSub: 'Las 3 fichas Oscuro / Claro rosa / Claro blanco en Descubrir. Sigue disponible en Ajustes.',
  },
  zh: {
    title: '版块可见性', sub: '一键隐藏或显示应用版块。内容保留在数据库中，重新启用后恢复。',
    home: '「首页」标签（信息流）', homeSub: '隐藏整个首页标签。故事按钮和菜单（AI 头像、添加好友）移至发现页；个人主页不再显示分享内容；专业广告在故事开头播放。',
    mymouv: 'MyMouv 版块（活动）', mymouvSub: '当主办方创建活动时自动重新显示。',
    myjob: 'MyJob 版块（招聘）', myjobSub: '当发布职位时自动重新显示。',
    films: '公共领域电影版块', visible: '显示', hidden: '隐藏', saved: '已保存',
    explore: '「探索整个应用」版块（发现）', exploreSub: '发现页底部通往 MyTalk、AI、分享和个人主页的彩色快捷方式。',
    themes: '显示模式选择（发现）', themesSub: '发现页中的深色 / 浅粉 / 浅白三块磁贴。设置中仍可选择。',
  },
};

const Row = ({ icon: Icon, label, sub, value, onToggle, testId, L }) => (
  <div className="flex items-start gap-3 p-4 rounded-2xl bg-white/5 border border-white/10" data-testid={testId}>
    <div className={`p-2 rounded-xl ${value ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}><Icon className="w-5 h-5" /></div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold">{label}</p>
      <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
    </div>
    <button onClick={onToggle} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 ${value ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`} data-testid={`${testId}-toggle`} aria-pressed={!value}>
      {value ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {value ? L.visible : L.hidden}
    </button>
  </div>
);

// Admin › Visibilité des sections (Accueil, MyMouv, MyJob, Films libres)
const SectionsVisibilityModal = ({ onClose }) => {
  const { adminLang } = useAdminLang();
  const L = T[adminLang] || T.fr;
  const [vis, setVis] = useState(null);

  useEffect(() => {
    axios.get('/api/settings/sections-visibility').then((r) => setVis(r.data)).catch(() => setVis({ home_tab: true, mymouv: true, myjob: true, films_libres: true }));
  }, []);

  const toggle = async (key) => {
    try {
      const r = await axios.post('/api/admin-ext/sections-visibility', { key, visible: vis[key] === false });
      setVis(r.data);
      refreshSectionsVisibility();
      toast.success(L.saved);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" data-testid="sections-visibility-modal">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold flex items-center gap-2"><Eye className="w-5 h-5 text-fuchsia-400" /> {L.title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="sections-visibility-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-400 mb-5 flex items-start gap-2"><Info className="w-4 h-4 mt-0.5 shrink-0" /> {L.sub}</p>
        {vis && (
          <div className="space-y-3">
            <Row icon={Home} label={L.home} sub={L.homeSub} value={vis.home_tab} onToggle={() => toggle('home_tab')} testId="visibility-home-tab" L={L} />
            <Row icon={CalendarDays} label={L.mymouv} sub={L.mymouvSub} value={vis.mymouv} onToggle={() => toggle('mymouv')} testId="visibility-mymouv" L={L} />
            <Row icon={Briefcase} label={L.myjob} sub={L.myjobSub} value={vis.myjob} onToggle={() => toggle('myjob')} testId="visibility-myjob" L={L} />
            <Row icon={Compass} label={L.explore} sub={L.exploreSub} value={vis.explore_app !== false} onToggle={() => toggle('explore_app')} testId="visibility-explore-app" L={L} />
            <Row icon={Palette} label={L.themes} sub={L.themesSub} value={vis.theme_modes !== false} onToggle={() => toggle('theme_modes')} testId="visibility-theme-modes" L={L} />
            <div className="pt-2">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1"><Film className="w-3.5 h-3.5" /> {L.films}</p>
              <FilmsLibresToggle />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionsVisibilityModal;
